import { useEffect, useRef } from 'react';

/**
 * useColyseusMessages — bind a map of `room.onMessage` handlers, with
 * automatic rebind across reconnects.
 *
 * The bind/poll/teardown shape was duplicated across DeathOverlay,
 * DrainOverlay, DuelOverlays, KillFeedPanel, KillCamBridge,
 * RemotePlayerVfxBridge, StatusEffectBridge, SimAnalyzerTab,
 * CraftingWindow, and TradeWindow. Every consumer wrote ~30 lines
 * of identical scaffolding around `(window as any).nm?.getRoom?.()`,
 * a 500ms `setInterval` to detect rebind, and per-handler
 * `room.onMessage(...)` calls. This hook centralizes the dance.
 *
 * Usage:
 *
 *   useColyseusMessages({
 *     playerDied: (msg) => setDeath({ ... }),
 *     playerRespawned: (msg) => setDeath(null),
 *   });
 *
 * The handler map can change between renders — internally we hold a
 * ref so the registered callback always reaches the latest closure
 * (no need to memoize handlers in the consumer).
 *
 * Reconnect semantics: when `window.nm.getRoom()` returns a different
 * Room object than the previously-bound one, we release our old
 * subscriptions before binding the new room. Colyseus 0.17 adds a
 * listener and returns its disposer; cleanup must invoke that disposer.
 * Other consumers' subscriptions are left intact. Missing rooms release
 * the previous binding on the next poll. Changing message types rebinds
 * immediately; changing callbacks alone uses the latest handler ref.
 *
 * Filtering by local sid: handlers receive the full message; do
 * `if (msg.victimSid !== room.sessionId) return;` inside the handler
 * for self-only logic. The hook deliberately doesn't bake sid filtering
 * in — the spectator-bridge pattern needs ALL events, and the per-call
 * filter is one line.
 *
 * Anti-scope: this hook does NOT subscribe to the EventBus republish
 * (`net:message:<type>`). Use it for direct `room.onMessage` callsites.
 * EventBus subscribers stay on `EventBus.getInstance().on(...)` since
 * those don't need the rebind dance (the Bus outlives the room).
 */

const POLL_INTERVAL_MS = 500;

type ColyseusMessageHandler = (message: any) => void;
type ColyseusMessageMap = Record<string, ColyseusMessageHandler>;

export function useColyseusMessages(handlers: ColyseusMessageMap): void {
  // Hold latest handlers in a ref so registered callbacks always reach
  // the freshest closure. Callers don't need to wrap their handlers in
  // `useCallback` — the registration happens once per room and reads
  // the ref on each fire.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  // A stable primitive dependency avoids rebinding for inline handler maps
  // or reordered keys, while supporting genuinely added/removed types.
  const messageTypes = JSON.stringify(Object.keys(handlers).sort());

  useEffect(() => {
    let boundRoom: any = null;
    let disposers: Array<() => void> = [];
    const types: string[] = JSON.parse(messageTypes);

    const unbind = () => {
      for (const dispose of disposers) dispose();
      disposers = [];
      boundRoom = null;
    };

    const tryBind = () => {
      const room = (globalThis as any).nm?.getRoom?.() ?? null;
      if (room === boundRoom) return;
      unbind();
      if (!room) return;
      boundRoom = room;

      for (const type of types) {
        // Wrapping the registered callback to read from the ref means
        // the consumer's handler can be replaced between renders
        // without re-registering. Captures `type` per iteration via
        // the for-of scope (no var-vs-let footgun).
        const dispose = room.onMessage(type, (msg: any) => {
          handlersRef.current[type]?.(msg);
        });
        disposers.push(dispose);
      }
    };

    tryBind();
    const interval = setInterval(tryBind, POLL_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      unbind();
    };
  }, [messageTypes]);
}
